import { format } from 'date-fns';
import { intlLocale, toTimestamp } from '..';
import { TimeSlot } from '../../types/model';
import { config } from '../../config';
import { mapTimeZone } from '../mapTimeZone';

function isSameDay(startDate: Date, endDate: Date) {
  return (
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getDate() === endDate.getDate()
  );
}

export function formatDate(date: Date | string) {
  date = new Date(date || 0);

  // Format date to certain language, specially made for Serbia Latin
  const langCode = config.dateFormat.languageCode || intlLocale;

  return Intl.DateTimeFormat(langCode, {
    weekday: config.dateFormat.weekday,
    month: config.dateFormat.month,
    day: config.dateFormat.day,
  }).format(date);
}

export function formatTime(
  date: Date | string,
  timeZone: string,
  showTimeZone: boolean = true
) {
  date = new Date(date || 0);

  const options: Intl.DateTimeFormatOptions = {
    hour: config.dateFormat.hour,
    minute: config.dateFormat.minute,
  };

  const timeZoneStr =
    config.showLocalTimeZone && showTimeZone
      ? ` ${mapTimeZone(timeZone, config.localtimezone)}`
      : '';

  return `${Intl.DateTimeFormat(intlLocale, options).format(
    date
  )}${timeZoneStr}`;
}

export function formatDateTime(date: Date | string, timeZone: string) {
  const p = document.createElement('p');
  p.innerHTML = `${formatDate(date)} ${formatTime(date, timeZone)}`;
  return p.innerHTML;
}

export function formatEventTimeSpan(
  startTimestamp: number,
  endTimestamp: number,
  startDate: string,
  endDate: string,
  timeZone: string
) {
  const utcStartDate = new Date(toTimestamp(startTimestamp));
  const utcEndDate = new Date(toTimestamp(endTimestamp));
  if (isSameDay(utcStartDate, utcEndDate)) {
    return `${formatDate(startDate)} ${formatTime(
      startDate,
      timeZone,
      false
    )}-${formatTime(endDate, timeZone)}`;
  } else {
    return `${formatDate(startDate)} ${formatTime(
      startDate,
      timeZone,
      false
    )} - ${formatDate(endDate)} ${formatTime(endDate, timeZone)}`;
  }
}

export function getTimeslotSummary(
  timeslots: Array<TimeSlot> = [],
  timeZone: string,
  t: Function
) {
  if (timeslots.length === 0) {
    return '';
  }

  return `${formatEventTimeSpan(
    timeslots[0].utcStartDate,
    timeslots[0].utcEndDate,
    timeslots[0].startDate,
    timeslots[0].endDate,
    timeZone
  )}${
    timeslots.length > 1 ? ` + ${timeslots.length - 1} ${t('event.more')}` : ''
  }`;
}

export function toDateTimeString(date: Date) {
  if (!date) {
    return '';
  }

  return format(date, "yyyy-MM-dd'T'HH:mm");
}
